import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Alert, AppShell } from '@zarya/ui';
import { useApprovalsSummary, useShell } from '@app/api/hooks/shell';
import { useCurrentUser } from '@app/auth/useCurrentUser';
import { useDiscoveryNow } from '@app/discovery';
import { errorView } from '@app/errors';
import { GlobalSearch } from './GlobalSearch';
import { MiddlewareDownNotice } from './MiddlewareDownNotice';
import { ShellRail } from './ShellRail';
import { ShellTopbar } from './ShellTopbar';
import { t } from '@app/i18n';

/** `Ctrl K` / `⌘K` abre a busca global (FR-014). */
function useAtalhoSearch(open: () => void) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k' || !(e.ctrlKey || e.metaKey) || e.altKey) return;
      e.preventDefault();
      open();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);
}

/**
 * Moldura do console (T2). É a rota-mãe de todas as telas: rail à esquerda,
 * topbar em cima e a tela da rota no `<Outlet />`.
 *
 * `getShell` alimenta o chip do executor e os contadores do menu; `getApprovalsSummary`,
 * o sino (FR-012, FR-013).
 */
export function Shell() {
  const shell = useShell();
  const approvals = useApprovalsSummary();
  const { estado, erro } = useCurrentUser();
  const [buscaAberta, setBuscaAberta] = useState(false);

  // Acompanhamento da varredura na **moldura** (29/08): ao abrir qualquer tela,
  // uma varredura em curso — a de outra pessoa, ou a do tique periódico — já
  // aparece no indicador de tarefas da topbar, e as listas se invalidam sozinhas
  // quando ela termina. Sem clique não há toast: o fecho é anunciado a quem pediu.
  //
  // O botão de T3/T4/T21 usa o mesmo hook; a tarefa no indicador é idempotente por
  // chave, então os dois montados juntos mostram **uma** tarefa.
  useDiscoveryNow();

  const openSearch = useCallback(() => setBuscaAberta(true), []);
  const closeSearch = useCallback(() => setBuscaAberta(false), []);

  useAtalhoSearch(openSearch);

  return (
    <AppShell
      rail={<ShellRail contadores={shell.data?.counters} />}
      topbar={
        <ShellTopbar
          middleware={shell.data?.middleware}
          pendentes={approvals.data?.pending ?? 0}
          onSearch={openSearch}
        />
      }
    >
      {estado === 'indisponivel' ? (
        <Alert tone="info" title={t('shell.session.unconfirmed.title')}>
          {/* O código do erro entra no meio da frase: as metades são chaves
              porque o `t()` devolve texto, não nó. */}
          {t('shell.session.unconfirmed.before')}
          {erro ? t('shell.session.unconfirmed.code', { code: errorView(erro).code }) : ''}
          {t('shell.session.unconfirmed.after')}
        </Alert>
      ) : null}
      {/* FR-125 (T142): aviso nas telas de escrita quando o executor está fora ou
          sem URL — antes de o usuário preencher. Mora na moldura para que
          **nenhuma** tela de escrita fique sem ele. */}
      <MiddlewareDownNotice />
      <Outlet />
      <GlobalSearch aberta={buscaAberta} onFechar={closeSearch} />
    </AppShell>
  );
}
